import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/api/client";
import {
  Search, ShoppingBag, KeyRound, ExternalLink, Sparkles, Loader2, AlertCircle,
  Store, CheckCircle2, XCircle, Save, DollarSign, Zap,
} from "lucide-react";

const MARKETPLACES = ["US", "UK", "DE", "FR", "IT", "ES", "JP", "IN", "CA", "AU", "BR", "MX"];

export default function PaapiOperations() {
  const [accounts, setAccounts] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [error, setError] = useState("");

  // Creds form
  const [accessKey, setAccessKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [marketplace, setMarketplace] = useState("US");
  const [savingCreds, setSavingCreds] = useState(false);

  // Search
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedAsins, setSelectedAsins] = useState(new Set());

  // Article
  const [creatingArticle, setCreatingArticle] = useState(false);

  // Test keys
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // {ok, message, elapsed_ms}

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(""), 3500); };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/network-sync/amazon-accounts");
      setAccounts(data);
      if (data.length && !activeId) {
        setActiveId(data[0].id);
        setMarketplace(data[0].paapi_marketplace || "US");
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const active = accounts.find((a) => a.id === activeId);

  const saveCreds = async () => {
    if (!active) return;
    setSavingCreds(true);
    try {
      await api.post("/network-sync/credentials", {
        account_id: active.id,
        access_key: accessKey,
        secret_key: secretKey,
        marketplace,
      });
      showToast("Keys saved (Fernet-encrypted). Try a live search below.");
      setAccessKey(""); setSecretKey("");
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingCreds(false);
    }
  };

  const testKeys = async () => {
    if (!active) return;
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/network-sync/paapi/test-keys", { account_id: active.id });
      setTestResult(data);
    } catch (e) {
      setTestResult({
        ok: false,
        code: "network_error",
        message: formatApiErrorDetail(e.response?.data?.detail) || e.message || "Could not reach the server.",
      });
    } finally {
      setTesting(false);
    }
  };

  const doSearch = async () => {
    if (!active) return;
    if (!query.trim()) { showToast("Enter a keyword first."); return; }
    setSearching(true); setResults(null); setError(""); setSelectedAsins(new Set());
    try {
      const { data } = await api.post("/network-sync/paapi/search", {
        account_id: active.id,
        keywords: query,
        item_count: 8,
      });
      setResults(data);
      if (!data.ok) {
        setError(`PA-API said: ${data.amazon_message || data.error || "Unknown"} · ${data.amazon_code || data.http_status || ""}`);
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSearching(false);
    }
  };

  const toggleAsin = (asin) => {
    setSelectedAsins((prev) => {
      const next = new Set(prev);
      next.has(asin) ? next.delete(asin) : next.add(asin);
      return next;
    });
  };

  const createArticle = async () => {
    if (!active) return;
    if (!query.trim()) { showToast("Enter a keyword topic first."); return; }
    if (!window.confirm(`Generate a comparison article from these live Amazon products?\n\nKeywords: "${query}"\nSelected ASINs: ${selectedAsins.size || "top 5 from search"}\n\n(~30-45s)`)) return;
    setCreatingArticle(true);
    try {
      const { data } = await api.post("/network-sync/paapi/create-article", {
        account_id: active.id,
        keywords: query,
        asins: Array.from(selectedAsins),
      });
      showToast(`Article created: "${data.title}" (${data.products_included} products)`);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCreatingArticle(false);
    }
  };

  return (
    <div className="space-y-8 fade-up">
      {toast && (
        <div data-testid="paapi-toast" className="fixed bottom-6 right-6 z-50 bg-sky-400 text-slate-950 px-5 py-3 rounded-md font-medium shadow-2xl">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="border-b border-slate-800 pb-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-2 flex items-center gap-2">
          <ShoppingBag className="w-3.5 h-3.5" /> — PA-API Operations
        </p>
        <h1 className="font-serif text-5xl leading-none text-sky-100 tracking-tight">
          Live Amazon <span className="italic text-sky-400/90">catalog</span>
        </h1>
        <p className="mt-3 text-neutral-400 max-w-2xl">
          Search real products, pull genuine prices and images, and generate a full comparison article from live PA-API 5 data in one click.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-16 text-neutral-500 flex items-center justify-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading Amazon accounts…
        </div>
      ) : accounts.length === 0 ? (
        <div className="border border-slate-800 rounded-xl p-12 text-center">
          <AlertCircle className="w-8 h-8 text-sky-400 mx-auto mb-3" />
          <p className="font-serif text-lg text-sky-100">No Amazon accounts connected.</p>
          <p className="text-sm text-neutral-500 mt-2">Connect one under Affiliate Studio → Connect and it'll show up here.</p>
        </div>
      ) : (
        <>
          {/* Account picker */}
          <div className="flex flex-wrap gap-2">
            {accounts.map((a) => (
              <button
                key={a.id}
                data-testid={`paapi-acc-${a.id}`}
                onClick={() => { setActiveId(a.id); setMarketplace(a.paapi_marketplace); setResults(null); setError(""); setTestResult(null); }}
                className={`px-4 py-2 rounded-full text-sm font-mono uppercase tracking-widest text-[11px] flex items-center gap-2 transition ${
                  activeId === a.id
                    ? "bg-sky-400 text-slate-950"
                    : "bg-slate-900 text-neutral-400 hover:text-sky-100 border border-slate-800"
                }`}
              >
                <Store className="w-3 h-3" />
                {a.affiliate_id} · {a.paapi_marketplace}
                {a.paapi_configured ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <XCircle className="w-3 h-3 text-red-400" />}
              </button>
            ))}
          </div>

          {active && (
            <>
              {/* PA-API keys form */}
              <div data-testid="paapi-keys-form" className="border border-slate-800 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <KeyRound className="w-5 h-5 text-sky-400" />
                  <div className="flex-1">
                    <h3 className="font-serif text-xl text-sky-100">PA-API keys · {active.affiliate_id}</h3>
                    <p className="text-xs text-neutral-500 mt-0.5">
                      Get yours in{" "}
                      <a href="https://webservices.amazon.com/paapi5/scratchpad/" target="_blank" rel="noreferrer" className="text-sky-300 underline">
                        Associates Central → Tools → PA-API
                      </a>. Fernet-encrypted at rest.
                    </p>
                  </div>
                  {active.paapi_configured && (
                    <span className="text-[10px] font-mono px-2 py-1 rounded-full border border-emerald-500/40 text-emerald-400 bg-emerald-500/5">● live</span>
                  )}
                </div>
                {active.paapi_configured && (
                  <div className="border border-emerald-500/20 bg-emerald-500/5 rounded p-3 mb-4 text-xs font-mono">
                    <p className="text-neutral-400">Access key: <span className="text-emerald-300">{active.paapi_masked || "—"}</span> · Marketplace: <span className="text-emerald-300">{active.paapi_marketplace}</span></p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">PA-API Access Key</label>
                    <input
                      data-testid="paapi-access-key"
                      value={accessKey}
                      onChange={(e) => setAccessKey(e.target.value)}
                      placeholder={active.paapi_configured ? "•••• leave blank to keep current" : "AKIAXXXXXXXXXXXXXXXX"}
                      className="w-full bg-slate-900 border border-slate-800 rounded px-3 py-2.5 text-sky-50 font-mono text-sm focus:border-sky-400 outline-none"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">Secret Key</label>
                    <input
                      data-testid="paapi-secret-key"
                      type="password"
                      value={secretKey}
                      onChange={(e) => setSecretKey(e.target.value)}
                      placeholder={active.paapi_configured ? "•••• leave blank to keep current" : ""}
                      className="w-full bg-slate-900 border border-slate-800 rounded px-3 py-2.5 text-sky-50 font-mono text-sm focus:border-sky-400 outline-none"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">Marketplace</label>
                    <select
                      data-testid="paapi-marketplace"
                      value={marketplace}
                      onChange={(e) => setMarketplace(e.target.value)}
                      className="w-full bg-slate-900 border border-slate-800 rounded py-2.5 px-3 text-sky-50 focus:border-sky-400 outline-none"
                    >
                      {MARKETPLACES.map((m) => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <button
                    data-testid="btn-save-paapi-keys"
                    onClick={saveCreds}
                    disabled={savingCreds}
                    className="py-2.5 px-5 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded font-medium flex items-center gap-2 disabled:opacity-60"
                  >
                    {savingCreds ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save keys
                  </button>
                  <button
                    data-testid="btn-test-paapi-keys"
                    onClick={testKeys}
                    disabled={testing || !active.paapi_configured}
                    title={!active.paapi_configured ? "Save your keys first" : "Ping Amazon PA-API to verify credentials"}
                    className="py-2.5 px-5 bg-slate-900 hover:bg-slate-800 text-sky-200 border border-sky-500/40 rounded font-medium flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Test my keys
                  </button>
                  {testResult && (
                    <div
                      data-testid={`paapi-test-result-${testResult.ok ? "ok" : "fail"}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs font-mono border ${
                        testResult.ok
                          ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-300"
                          : "border-red-500/40 bg-red-500/5 text-red-300"
                      }`}
                    >
                      {testResult.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                      <span className="max-w-md">{testResult.message}</span>
                      {typeof testResult.elapsed_ms === "number" && testResult.elapsed_ms > 0 && (
                        <span className="opacity-60">· {testResult.elapsed_ms}ms</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Live search */}
              <div className="border border-slate-800 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Search className="w-5 h-5 text-sky-400" />
                  <h3 className="font-serif text-xl text-sky-100">Live product search</h3>
                </div>
                <div className="flex gap-2">
                  <input
                    data-testid="paapi-query"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && doSearch()}
                    placeholder="e.g. best mechanical keyboards"
                    className="flex-1 bg-slate-900 border border-slate-800 rounded px-4 py-2.5 text-sky-50 focus:border-sky-400 outline-none"
                  />
                  <button
                    data-testid="btn-paapi-search"
                    onClick={doSearch}
                    disabled={searching || !active.paapi_configured}
                    className="py-2.5 px-5 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded font-medium flex items-center gap-2 disabled:opacity-60"
                  >
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    Search live
                  </button>
                </div>
                {!active.paapi_configured && (
                  <p className="text-xs text-sky-400/80 mt-2 font-mono">Add PA-API keys above to enable live search.</p>
                )}
              </div>

              {error && (
                <div className="border border-red-500/40 bg-red-500/5 text-red-300 px-4 py-3 text-sm rounded flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* Search results */}
              {results && results.ok && results.items && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="font-mono text-[11px] uppercase tracking-widest text-neutral-500">
                      {results.items.length} live results from Amazon
                    </p>
                    <button
                      data-testid="btn-create-article"
                      onClick={createArticle}
                      disabled={creatingArticle}
                      className="py-2.5 px-5 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded font-medium flex items-center gap-2 disabled:opacity-60"
                    >
                      {creatingArticle ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      {selectedAsins.size > 0 ? `Create article with ${selectedAsins.size} selected` : "Create article from top 5"}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {results.items.map((item) => {
                      const selected = selectedAsins.has(item.asin);
                      return (
                        <div
                          key={item.asin}
                          data-testid={`paapi-item-${item.asin}`}
                          onClick={() => toggleAsin(item.asin)}
                          className={`border rounded-lg p-4 cursor-pointer transition ${selected ? "border-sky-400 bg-sky-500/5" : "border-slate-800 hover:border-sky-400/40"}`}
                        >
                          {item.image_url ? (
                            <img src={item.image_url} alt={item.title} className="w-full h-32 object-contain rounded mb-3 bg-slate-950/60" />
                          ) : (
                            <div className="w-full h-32 rounded mb-3 bg-slate-900 flex items-center justify-center text-slate-700">
                              <ShoppingBag className="w-8 h-8" />
                            </div>
                          )}
                          <p className="text-xs text-neutral-400 font-mono">{item.asin}</p>
                          <h4 className="font-serif text-sm text-sky-100 mt-1 line-clamp-3 leading-snug">{item.title}</h4>
                          <div className="mt-2 flex items-center justify-between">
                            <p className="text-sky-300 font-mono flex items-center gap-1"><DollarSign className="w-3 h-3" />{item.price || "—"}</p>
                            {item.detail_url && (
                              <a
                                href={item.detail_url}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-sky-400 hover:text-sky-200"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
